/**
 * ConversationView — the chat surface (message list + turn progress +
 * load-older sentinel). Composer is rendered separately so callers can
 * place it where they want (sticky bottom in surface chrome).
 *
 * Distilled from `apps/agent/client/src/pages/chat.tsx::Chat` (53-412).
 */

import type { ActionCardResolution } from '@ordo/agent-tools'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'

import type { ChatMessage, TurnState } from '../types'
import { MessageTurn } from './message-turn'

export interface ConversationViewProps {
  messages: ChatMessage[]
  turnState: TurnState
  hasMore: boolean
  isLoadingOlder: boolean
  onLoadOlder: () => void
  onDecideApproval?: (toolUseId: string, decision: 'approve' | 'skip') => void | Promise<void>
  onResolveCard?: (cardId: string, resolution: ActionCardResolution) => void | Promise<void>
  /** Retry a failed user message — surfaces an inline Retry button
   *  on errored bubbles. */
  onRetry?: (messageId: string) => void | Promise<void>
  /** Click handler for a suggested-reply chip on the latest assistant
   *  bubble. Sends the chip text as the next user message. */
  onSendSuggestion?: (text: string) => void | Promise<void>
  empty?: React.ReactNode
}

export function ConversationView({
  messages,
  turnState,
  hasMore,
  isLoadingOlder,
  onLoadOlder,
  onDecideApproval,
  onResolveCard,
  onRetry,
  onSendSuggestion,
  empty,
}: ConversationViewProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Sticky-bottom on new messages.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, turnState])

  // IntersectionObserver sentinel for lazy-load.
  useEffect(() => {
    if (!hasMore) {
      return
    }
    const el = sentinelRef.current
    if (!el) {
      return
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !isLoadingOlder) {
            onLoadOlder()
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasMore, isLoadingOlder, onLoadOlder])

  if (messages.length === 0 && empty) {
    return <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-12">{empty}</div>
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
      {hasMore ? (
        <div ref={sentinelRef} className="flex items-center justify-center py-2">
          {isLoadingOlder ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
        </div>
      ) : messages.length > 0 ? (
        // Show "Beginning of conversation" once we've loaded everything
        // — matches agent.ordo.com's canonical rendering.
        <div className="py-1 text-center text-[11px] text-muted-foreground/70">Beginning of conversation</div>
      ) : null}
      {messages.map((m, idx) => (
        <MessageTurn
          key={m.id}
          message={m}
          onDecideApproval={onDecideApproval}
          onResolveCard={onResolveCard}
          onRetry={onRetry}
          // Only the LAST assistant turn gets click-to-send suggestions —
          // older turns' suggestions reflect a stale conversation state.
          onSendSuggestion={idx === messages.length - 1 && turnState === 'idle' ? onSendSuggestion : undefined}
        />
      ))}
      <div ref={bottomRef} />
    </div>
  )
}
